import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { Link } from 'react-router-dom';
import { FaTrash, FaSync, FaRocket, FaTimes, FaSearch, FaCalculator, FaBoxOpen, FaPlus, FaSpinner, FaClock, FaExclamationCircle, FaCheckCircle, FaUndoAlt, FaClipboardList, FaMinusCircle } from 'react-icons/fa';
import { MdAssignment, MdApartment, MdNumbers, MdScale, MdDownload, MdSelectAll } from 'react-icons/md';
import SalesUnitSelect from '../../../components/app/crm/release-forecast/components/SalesUnitSelect';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { toast } from 'react-toastify';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import ActionCell, { DeletedActionCell } from '../../../components/app/crm/release-forecast/components/ActionCell';
import {
  getCsdForecasts,
  releaseCsdToPlanning, backlogCsdForecast, unbacklogCsdForecast, restoreCsdForecasts, purgeCsdForecasts, deleteCsdRows,
  addToReleaseList, removeFromReleaseList, getCsdTabRecords,
  setCsdPage, setCsdPageSize, exportCsdForecasts,
  recalculateCsdForecast, recalculateCsdPeriod, getCsdRecalculateStatus
} from 'redux/crm/releaseForecastSlice';
import { confirmAlert, LoadingSpinner, MonthYearPicker, Button, Pagination } from 'components/common';
import { escapeHtml, formatDate, formatNumber } from 'helpers/utils';
import CsdForecastEditForm from '../../../components/app/crm/release-forecast/csd/CsdForecastEditForm';
import PlanningReleaseConfirmDialog from '../../../components/app/crm/release-forecast/csd/PlanningReleaseConfirmDialog';
import RecalculateFailuresDialog from '../../../components/app/crm/release-forecast/csd/RecalculateFailuresDialog';
import CsdImportModal from '../../../components/app/crm/release-forecast/csd/CsdImportModal';
import { earliestExpectedDate, getForecastMonthLabels } from '../../../components/app/crm/release-forecast/components/helpers';
import useDebouncedForecastSearch from '../../../components/app/crm/release-forecast/components/useDebouncedForecastSearch';
import usePersistentRowSelection from '../../../components/app/crm/release-forecast/components/usePersistentRowSelection';

dayjs.extend(customParseFormat);

// ── Grid CSS (inject once) ────────────────────────────────────────────────────
const gridStyles = `
  .ag-header-cell-menu-button { display: none !important; }
  .ag-row[row-type="data"] { border-bottom: 1px solid #e5e7eb; }
  .csd-m1-header { background-color: #fef3c7 !important; }
  .csd-m1-cell   { background-color: #fffbeb !important; }
  .csd-m2-header { background-color: #dbeafe !important; }
  .csd-m2-cell   { background-color: #eff6ff !important; }
  .csd-m3-header { background-color: #dcfce7 !important; }
  .csd-m3-cell   { background-color: #f0fdf4 !important; }
  .csd-weight-header { background-color: #fef3c7 !important; }
  .csd-weight-cell   { background-color: #fffbeb !important; }
`;

// A column's own cellClass / headerClass replace the classes type 'numericColumn'
// brings, which is what right-aligns them — so they are listed again here.
const WEIGHT_COLUMN = {
  type: 'numericColumn',
  headerClass: ['csd-weight-header', 'ag-right-aligned-header'],
  cellClass: ['csd-weight-cell', 'ag-right-aligned-cell', 'tw-tabular-nums'],
};

const fmt = (n) => (n != null && n > 0)
  ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  : '';

// Every tab is filtered — and counted — by the server (Csd::TAB_CONDITIONS), so a
// tab and its badge cover all pages, not just the 50 rows on screen.
const TABS = [
  { key: '',                 label: 'All',         Icon: MdSelectAll },
  { key: 'release_list',     label: 'Release list', Icon: FaClipboardList },
  { key: 'unreleased',       label: 'Unreleased',  Icon: FaClock },
  { key: 'backlog',          label: 'Backlog',     Icon: FaBoxOpen },
  { key: 'not_recalculated', label: 'Not Recalc.', Icon: FaExclamationCircle },
  { key: 'released',         label: 'Released',    Icon: FaCheckCircle },
  { key: 'deleted',          label: 'Deleted',     Icon: FaTrash },
];

const getDefaultPeriod = () => {
  const p = new URLSearchParams(window.location.search);
  return p.get('period') || dayjs().format('MMM-YYYY');
};

// ── component ─────────────────────────────────────────────────────────────────
const ReleaseForecastCsdPage = () => {
  const dispatch = useDispatch();
  const gridRef  = useRef(null);

  const [pageReady,              setPageReady]              = useState(false);
  const [showImport,             setShowImport]             = useState(false);
  const [showEditForm,           setShowEditForm]           = useState(false);
  const [selectedRecord,         setSelectedRecord]         = useState(null);
  const [planningReleaseTarget,  setPlanningReleaseTarget]  = useState(null);
  const [viewMode,               setViewMode]               = useState('project');
  const [quickFilter,            setQuickFilter]            = useState('');
  const [recalculateJob,         setRecalculateJob]         = useState(null);
  // Failures of the last recalculation, kept so their reasons stay reachable
  // after the toast has gone.
  const [recalculateFailures,    setRecalculateFailures]    = useState(null);
  const [showRecalcFailures,     setShowRecalcFailures]     = useState(false);
  const recalculatePollRef = useRef(null);

  const [filters, setFilters] = useState({
    period:        getDefaultPeriod(),
    sales_unit_id: [],
  });
  const { searchInput, setSearchInput, searchQuery, clearSearch } = useDebouncedForecastSearch();

  const serverState   = quickFilter;
  const isDeletedView = serverState === 'deleted';

  // Ticks are for acting now (add to the Release list, Backlog, Delete) and clear
  // after each action. What is being prepared for release is the Release list,
  // kept on the server per period — not the ticks.
  const { selectedRows, onSelectionChanged, restoreSelection, clearSelection, removeWhere } =
    usePersistentRowSelection(gridRef);
  // Picks were briefly kept in sessionStorage; drop what is left of them.
  useEffect(() => {
    try {
      Object.keys(window.sessionStorage).filter((k) => k.startsWith('csd-forecast-selection:'))
        .forEach((k) => window.sessionStorage.removeItem(k));
    } catch { /* storage blocked: nothing to clean */ }
  }, []);
  // A view switch changes the row ids; never carry picks into another period.
  useEffect(() => { clearSelection(); }, [clearSelection, filters.period, viewMode, isDeletedView]);

  const csdState      = useSelector((s) => s.crm_release_forecast.csdData);
  const exportLoading = useSelector((s) => s.crm_release_forecast.exportLoading);

  const allRowsData = csdState.allData?.data;
  const allRows    = useMemo(() => allRowsData || [], [allRowsData]);
  const totalRows  = csdState.allData?.totalRows || 0;
  const loading    = csdState.loading;
  const page       = csdState.allData?.currentPage || 1;
  const perPage    = csdState.allData?.pageSize || 50;
  const totalPages = csdState.allData?.totalPagesCount || Math.ceil(totalRows / perPage);

  // Month labels must be before buildingFlatRows
  const monthLabels = useMemo(() => getForecastMonthLabels(filters.period), [filters.period]);

  const rows = allRows;
  const tabCounts = csdState.tabCounts;

  // ── Derived counts ───────────────────────────────────────────────────────────
  // Released and Backlog forecasts keep every row.
  const deletableRows  = selectedRows.filter((r) => !r.released_to_planning_at && r.status !== 'backlog');
  const deletableCount = deletableRows.length;
  // Rows eligible for the two bulk actions, hoisted so the toolbar does not
  // recompute the same filter three times while rendering.
  const backlogableRows = selectedRows.filter((r) => !r.released_to_planning_at && r.status !== 'backlog');
  const backlogableCount = [...new Set(backlogableRows.map((r) => r.csd_id || r.id))].length;


  // ── Shared flat-row builder ──────────────────────────────────────────────────
  const makeFlatRow = useCallback((record, b, m, mi) => {
    const transDate = m.trans_date;
    return {
      csd_id:             record.id,
      month_index:        m.month_index ?? (mi + 1),
      building_id:        b?.building_id ?? null,
      trans_date_group:   transDate ? dayjs(transDate).format('MMM-YYYY') : (m.month_label || monthLabels[mi] || `M+${mi + 1}`),
      project:            record.project,
      // The building row's own release date; the project date only for legacy rows.
      expected_date:      earliestExpectedDate([m], record.expected_date_of_release_for_fabrication),
      percent_change:     record.percent_change_of_release,
      released_at:             record.released_to_planning_at,
      released_to_planning_at: record.released_to_planning_at,
      released_by:             record.released_to_planning_by,
      // Deleted tab: the row's own deletion (a stub row: the forecast's).
      deleted_at:              m.deleted_at || record.deleted_at,
      deleted_by:              m.deleted_by || record.deleted_by,
      // The forecast months this grid row stands for — what delete / restore /
      // purge act on, and nothing else.
      monthly_ids:             m.id ? [m.id] : [],
      row_counts:              record.row_counts,
      release_listed_at:       record.release_listed_at,
      sales_incharge:     record.project?.sales_incharge,
      sales_unit_name:    record.sales_unit?.name,
      sales_unit_region:  record.sales_unit?.region,
      remark:              record.remark,
      status:              record.status,
      permissions:         record.permissions,
      building_name:       b?.building_name || null,
      project_status_name: m.project_status_name || null,
      phase:               m.phase || null,
    };
  }, [monthLabels]);

  // ── Project flat rows ────────────────────────────────────────────────────────
  // Show ALL CSD records regardless of weight so they are manageable.
  // CSDs with no building data yet get a single stub row so users can edit/delete them.
  const projectFlatRows = useMemo(() => {
    return rows.flatMap((record) => {
      const buildings = record.buildings || [];

      // Collect unique month_indexes that have real DB records (id != null)
      const monthIndexSet = new Set();
      buildings.forEach((b) => (b.months || []).forEach((m) => { if (m.id != null) monthIndexSet.add(m.month_index); }));
      const sortedMonthIndexes = [...monthIndexSet].sort((a, b) => a - b);

      if (sortedMonthIndexes.length === 0) {
        // Stub row: CSD exists but has no monthly building data yet
        return [{
          rowId:              `proj-${record.id}-stub`,
          ...makeFlatRow(record, null, {}, 0),
          _stub:              true,
          building_name:      null,
          built_up_weight:    0,
          cold_formed_weight: 0,
          sheeting_weight:    0,
        }];
      }

      return sortedMonthIndexes.map((monthIdx) => {
        const monthsInMonth = buildings.flatMap((b) => b.months || []).filter((m) => m.month_index === monthIdx && m.id != null);
        const refMonth = monthsInMonth[0] || {};
        const sum = (key) => monthsInMonth.reduce((s, m) => s + (m[key] || 0), 0);
        return {
          rowId:              `proj-${record.id}-m${monthIdx}`,
          ...makeFlatRow(record, null, refMonth, monthIdx - 1),
          monthly_ids:        monthsInMonth.map((m) => m.id),
          // A project row stands for all its buildings in this month: earliest of their dates.
          expected_date:      earliestExpectedDate(monthsInMonth, record.expected_date_of_release_for_fabrication),
          building_name:      null,
          built_up_weight:    sum('built_up_weight'),
          cold_formed_weight: sum('cold_formed_weight'),
          sheeting_weight:    sum('sheeting_weight'),
        };
      });
    });
  }, [rows, makeFlatRow]);

  // ── Building flat rows ───────────────────────────────────────────────────────
  const buildingFlatRows = useMemo(() => {
    return rows.flatMap((record) => {
      const buildings = record.buildings || [];

      if (buildings.length === 0) {
        // Stub row: CSD exists but has no buildings configured yet
        return [{
          rowId:              `bld-${record.id}-stub`,
          _stub:              true,
          ...makeFlatRow(record, null, {}, 0),
          built_up_weight:    0,
          cold_formed_weight: 0,
          sheeting_weight:    0,
          source_document:    null,
        }];
      }

      return buildings.flatMap((b) =>
        (b.months || []).filter((m) => m.id != null).map((m) => ({
          // A Deleted tab month can hold several deleted rows: keyed by row there.
          rowId:              isDeletedView ? `bld-${record.id}-${m.id}` : `bld-${record.id}-${b.building_id}-m${m.month_index}`,
          ...makeFlatRow(record, b, m, m.month_index - 1),
          built_up_weight:    m.built_up_weight    || 0,
          cold_formed_weight: m.cold_formed_weight || 0,
          sheeting_weight:    m.sheeting_weight    || 0,
          source_document:    m.source_document,
        }))
      );
    });
  }, [rows, makeFlatRow, isDeletedView]);

  // ── Shared group injection: adds _seq + counts unique projects ───────────────
  const injectGroups = useCallback((flatRows, prefix) => {
    if (!flatRows.length) return [];
    const grouped = {};
    flatRows.forEach((r) => {
      const k = r.trans_date_group || dayjs().format('MMM-YYYY');
      if (!grouped[k]) grouped[k] = [];
      grouped[k].push(r);
    });
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      try { return dayjs(a, 'MMM-YYYY').valueOf() - dayjs(b, 'MMM-YYYY').valueOf(); }
      catch { return a.localeCompare(b); }
    });
    const result = [];
    let seq = 1;
    sortedKeys.forEach((month) => {
      const monthRows = grouped[month];
      const uniqueProjects = new Set(monthRows.map((r) => r.csd_id)).size;
      result.push({
        rowId:              `${prefix}-${month}`,
        _is_group:          true,
        trans_date_group:   month,
        _project_count:     uniqueProjects,
        built_up_weight:    monthRows.reduce((s, r) => s + r.built_up_weight,    0),
        cold_formed_weight: monthRows.reduce((s, r) => s + r.cold_formed_weight, 0),
        sheeting_weight:    monthRows.reduce((s, r) => s + r.sheeting_weight,    0),
      });
      monthRows.forEach((row) => result.push({ ...row, _seq: seq++ }));
    });
    return result;
  }, []);

  const projectGroupedRows = useMemo(() => injectGroups(projectFlatRows,  'grp-proj'), [projectFlatRows,  injectGroups]);
  const buildingGroupedRows = useMemo(() => injectGroups(buildingFlatRows, 'grp-bld'),  [buildingFlatRows, injectGroups]);

  const gridRows = viewMode === 'building' ? buildingGroupedRows : projectGroupedRows;

  const hiddenSelectedCount = useMemo(() => {
    const visibleIds = new Set(gridRows.map((r) => r.rowId));
    return selectedRows.filter((r) => !visibleIds.has(r.rowId)).length;
  }, [gridRows, selectedRows]);

  // ── Grand total pinned footer (both views) — unique project count ─────────
  const pinnedBottomRow = useMemo(() => {
    const src = viewMode === 'building' ? buildingFlatRows : projectFlatRows;
    const uniqueProjects = new Set(src.map((r) => r.csd_id)).size;
    return [{
      rowId:              'grand-total',
      _is_footer:         true,
      _project_count:     uniqueProjects,
      built_up_weight:    src.reduce((s, r) => s + (r.built_up_weight    || 0), 0),
      cold_formed_weight: src.reduce((s, r) => s + (r.cold_formed_weight || 0), 0),
      sheeting_weight:    src.reduce((s, r) => s + (r.sheeting_weight    || 0), 0),
    }];
  }, [viewMode, buildingFlatRows, projectFlatRows]);

  // ── Fetch helpers ────────────────────────────────────────────────────────────
  const buildFetchParams = useCallback((pg = page) => {
    const p = { page: pg, per_page: perPage };
    if (filters.period)        p.period       = filters.period;
    if (searchQuery)           p.q = searchQuery;
    if (serverState)           p.state = serverState;
    // Tab counts change with the filters or after an action — both reload page 1;
    // turning pages does not need them again.
    if (pg === 1)              p.with_counts = 1;
    if (filters.sales_unit_id.length) p.sales_unit_id = filters.sales_unit_id.join(',');
    return p;
  }, [page, perPage, filters, searchQuery, serverState]);

  const reload = useCallback(
    (pg = page) => dispatch(getCsdForecasts({ params: buildFetchParams(pg) })),
    [dispatch, buildFetchParams, page]
  );

  // back to page 1 on a query change; reload's identity also tracks page, which is fetched below
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(1); }, [filters, searchQuery, serverState]);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    reload();
  // page turns only; query changes already reload page 1 above, so reload's identity must not retrigger
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  useEffect(() => { dispatch(getActiveSalesUnits()); }, [dispatch]);

  // Inject grid CSS once
  useEffect(() => {
    const id = 'csd-grid-styles';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id; el.textContent = gridStyles;
      document.head.appendChild(el);
    }
  }, []);

  const setFilter = (key, val) => {
    setFilters((prev) => ({ ...prev, [key]: val }));
  };
  const hasFilters = searchInput !== '' || filters.sales_unit_id.length > 0 || quickFilter !== '';
  const clearFilters = () => {
    clearSearch();
    setFilters((prev) => ({ ...prev, sales_unit_id: [] }));
    setQuickFilter('');
  };

  // ── Project-level handlers ───────────────────────────────────────────────────

  // Deletes this grid row only: its own month rows (a stub row: the forecast).
  const handleDelete = useCallback((record) => {
    const monthLabel = record.monthly_ids?.length
      ? ` — <strong class="tw-text-primary-600">${record.trans_date_group || `Month ${record.month_index}`}</strong>${record.building_name ? ` (${record.building_name})` : ''}`
      : '';

    confirmAlert({
      title: 'Delete Forecast',
      message: `Delete forecast for <strong class="tw-text-blue-600">${escapeHtml(record.project?.name || '')}</strong>${monthLabel}?<br/><span class="tw-text-xs tw-text-gray-500">Only this row — restore it from the Deleted tab.</span>`,
      closeOnEscape: true, closeOnClickOutside: false,
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            const { payload } = await dispatch(deleteCsdRows({ rows: [record] }));
            if (payload?.done) removeWhere((r) => r.rowId === record.rowId);
            reload(1);
          },
        },
        { label: 'No', onClick: () => {} },
      ],
    });
  }, [dispatch, reload, removeWhere]);

  const handleRecalculate = useCallback((record) => {
    const csdId      = record.id || record.csd_id;
    const monthIndex = record.month_index ?? null;
    const buildingId = record.building_id ?? null;
    const monthLabel = monthIndex != null
      ? ` for <strong class="tw-text-primary-600">${record.trans_date_group || `Month ${monthIndex}`}</strong>${buildingId ? ` (${record.building_name})` : ''}`
      : '';

    confirmAlert({
      title: 'Recalculate',
      message: `Recalculate weights for <strong class="tw-text-blue-600">${record.project?.name}</strong>${monthLabel}? Input data will be overwritten.`,
      closeOnEscape: true, closeOnClickOutside: false,
      buttons: [
        {
          label: 'Yes',
          variant: 'primary',
          onClick: async () => {
            try {
              await dispatch(recalculateCsdForecast({
                csdId,
                monthIndex: monthIndex ?? undefined,
                buildingId: buildingId ?? undefined,
              })).unwrap();
              reload(1);
            } catch {
              toast.error('Recalculate failed');
            }
          },
        },
        { label: 'No', onClick: () => {} },
      ],
    });
  }, [dispatch, reload]);


  // ── Building-level handlers (resolve parent CSD record) ──────────────────────
  const handleBuildingEdit    = useCallback((r) => { const rec = allRows.find((x) => x.id === r.csd_id); if (rec) { setSelectedRecord(rec); setShowEditForm(true); } }, [allRows]);
  const handleBuildingDelete  = useCallback((r) => handleDelete(r), [handleDelete]);
  const handleBuildingRecalc  = useCallback((r) => handleRecalculate({
    id: r.csd_id, csd_id: r.csd_id,
    month_index: r.month_index, building_id: r.building_id,
    building_name: r.building_name, trans_date_group: r.trans_date_group,
    project: r.project,
  }), [handleRecalculate]);

  // ── Bulk handlers ────────────────────────────────────────────────────────────
  // Deletes exactly the selected rows — never the rest of their forecast.
  const handleBulkDelete = useCallback(() => {
    if (!deletableRows.length) { toast.error('Released and Backlog forecasts cannot be deleted.'); return; }
    const skipped = selectedRows.length - deletableRows.length;
    confirmAlert({
      title: 'Delete',
      message: `Move the <strong>${deletableRows.length}</strong> selected row(s) to Deleted?<br/><span class="tw-text-xs tw-text-gray-500">Only these rows; the other months and buildings stay.${skipped ? ` ${skipped} released / backlog row(s) are left as they are.` : ''} Restore them from the Deleted tab.</span>`,
      closeOnEscape: true, closeOnClickOutside: false,
      buttons: [
        { label: 'Yes', onClick: async () => {
          const { payload } = await dispatch(deleteCsdRows({ rows: deletableRows }));
          if (payload?.done) { const ids = new Set(deletableRows.map((r) => r.rowId)); removeWhere((r) => ids.has(r.rowId)); }
          reload(1);
        } },
        { label: 'No', onClick: () => {} },
      ],
    });
  }, [selectedRows, deletableRows, dispatch, reload, removeWhere]);

  // ── Deleted tab ──────────────────────────────────────────────────────────────
  const runOnDeleted = useCallback((records, { title, message, label, thunk }) => {
    if (!records.length) return;
    confirmAlert({
      title, message: message(records.length), closeOnEscape: true, closeOnClickOutside: false,
      buttons: [
        { label, onClick: async () => {
          await dispatch(thunk({ rows: records }));
          // What is still deleted comes back from the server; drop the rest.
          const ids = new Set(records.map((r) => r.rowId));
          removeWhere((r) => ids.has(r.rowId));
          reload(1);
        } },
        { label: 'Cancel', onClick: () => {} },
      ],
    });
  }, [dispatch, reload, removeWhere]);

  const handleRestore = useCallback((records) => runOnDeleted(records, {
    title: 'Restore', label: 'Restore', thunk: restoreCsdForecasts,
    message: (n) => `Restore the <strong>${n}</strong> selected row(s)? Only these rows go back to the list.`,
  }), [runOnDeleted]);

  const handlePurge = useCallback((records) => runOnDeleted(records, {
    title: 'Delete permanently', label: 'Delete permanently', thunk: purgeCsdForecasts,
    message: (n) => `Permanently delete the <strong>${n}</strong> selected row(s)? Only these rows.<br/><span class="tw-text-xs tw-text-red-600">This cannot be undone.</span>`,
  }), [runOnDeleted]);

  // Row action: Backlog at once (no dialog) with an Undo in the toast.
  const handleBacklogNow = useCallback(async (row) => {
    const id = row.csd_id || row.id;
    const name = row.project?.name || 'Project';
    const done = await dispatch(backlogCsdForecast({ id, silent: true }));
    if (done.error) return;
    removeWhere((r) => (r.csd_id || r.id) === id);
    reload(1);
    toast.success(({ closeToast }) => (
      <span className="tw-inline-flex tw-items-center tw-gap-2">
        <span>{name} moved to Backlog</span>
        <button type="button" className="tw-px-2 tw-py-0.5 tw-rounded tw-border tw-border-solid tw-border-emerald-300 tw-bg-white tw-text-emerald-700 tw-text-xs tw-font-semibold tw-cursor-pointer"
          onClick={async () => { closeToast(); await dispatch(unbacklogCsdForecast({ id })); reload(1); }}>
          Undo
        </button>
      </span>
    ), { autoClose: 8000 });
  }, [dispatch, reload, removeWhere]);

  const listableIds = useMemo(() => [...new Set(selectedRows
    .filter((r) => !r.released_to_planning_at && r.status !== 'backlog' && !r.release_listed_at)
    .map((r) => r.csd_id || r.id))], [selectedRows]);
  const listedIds = useMemo(() => [...new Set(selectedRows
    .filter((r) => r.release_listed_at).map((r) => r.csd_id || r.id))], [selectedRows]);

  const handleAddToReleaseList = useCallback(async () => {
    await dispatch(addToReleaseList({ csdIds: listableIds }));
    clearSelection();
    reload(1);
  }, [dispatch, listableIds, clearSelection, reload]);

  const handleRemoveFromReleaseList = useCallback(async (csdIds) => {
    await dispatch(removeFromReleaseList({ csdIds }));
    clearSelection();
    reload(1);
  }, [dispatch, clearSelection, reload]);

  const handleClearReleaseList = useCallback(() => {
    confirmAlert({
      title: 'Clear the Release list',
      message: `Take every project of <strong>${escapeHtml(filters.period)}</strong> off the Release list?<br/><span class="tw-text-xs tw-text-gray-500">The forecasts themselves do not change.</span>`,
      closeOnEscape: true, closeOnClickOutside: false,
      buttons: [
        { label: 'Clear list', onClick: async () => { await dispatch(removeFromReleaseList({ period: filters.period, all: true })); clearSelection(); reload(1); } },
        { label: 'Cancel', onClick: () => {} },
      ],
    });
  }, [dispatch, filters.period, clearSelection, reload]);

  const handleBulkBacklog = useCallback(() => {
    const eligible = selectedRows.filter((r) => !r.released_to_planning_at && r.status !== 'backlog');
    if (!eligible.length) { toast.error('No eligible rows.'); return; }
    // Deduplicate by CSD id — in building view mode multiple building rows share the same project CSD
    const uniqueCsdIds = [...new Set(eligible.map((r) => r.csd_id || r.id).filter(Boolean))];
    confirmAlert({
      title: 'Mark as Backlog',
      message: `Mark <strong>${uniqueCsdIds.length}</strong> project(s) as Backlog?<br/><span class="tw-text-xs tw-text-gray-500">Each project will be marked as Backlog regardless of view mode.</span>`,
      closeOnEscape: true, closeOnClickOutside: false,
      buttons: [
        { label: 'Yes', onClick: async () => { await Promise.all(uniqueCsdIds.map((id) => dispatch(backlogCsdForecast({ id, silent: id !== uniqueCsdIds[0] })).unwrap().catch(() => {}))); clearSelection(); reload(1); } },
        { label: 'Cancel', onClick: () => {} },
      ],
    });
  }, [selectedRows, dispatch, reload, clearSelection]);

  // ── Release to Planning ──────────────────────────────────────────────────────
  // Releases the Release list of the period — or, while it is empty, every
  // unreleased project of the period (the previous behaviour). Both are loaded
  // from the server, so every page is included, not only the rows on screen.
  const releaseListCount = tabCounts?.release_list || 0;
  const releaseSource = releaseListCount > 0 ? 'release_list' : 'unreleased';
  const releaseCount = tabCounts?.[releaseSource] || 0;
  const handlePlanningReleaseClick = useCallback(async () => {
    if (!filters.period) { toast.error('Pick a period first — a release is per period.'); return; }
    const { payload, error } = await dispatch(getCsdTabRecords({ period: filters.period, state: releaseSource }));
    if (error) return;
    if (!payload.length) { toast.error('Nothing to release for this period.'); return; }
    setPlanningReleaseTarget({ records: payload, period: filters.period });
  }, [dispatch, filters.period, releaseSource]);

  const handlePlanningReleaseConfirm = useCallback((period, csd_ids, email) => {
    if (!period) { toast.error('Period is required'); return; }
    dispatch(releaseCsdToPlanning({ period, csd_ids, ...email })).unwrap()
      .then((result) => { toast.success(`✅ Released ${result.record_count} record(s) for ${period}`); setPlanningReleaseTarget(null); clearSelection(); reload(1); })
      .catch((e) => toast.error(e?.message || '❌ Release failed'));
  }, [dispatch, reload, clearSelection]);

  // ── Recalculate period ───────────────────────────────────────────────────────
  const stopPolling = useCallback(() => { if (recalculatePollRef.current) { clearInterval(recalculatePollRef.current); recalculatePollRef.current = null; } }, []);
  useEffect(() => () => stopPolling(), [stopPolling]);

  const startPolling = useCallback((taskId) => {
    stopPolling();
    recalculatePollRef.current = setInterval(async () => {
      try {
        const response = await dispatch(getCsdRecalculateStatus({ taskId })).unwrap();
        const { status, result, error } = response;
        if (status === 'done') {
          stopPolling();
          setRecalculateJob(null);
          const message = result?.message || 'Recalculation completed';
          if (result?.records_failed > 0) {
            // The reason for each failure lives in failed_records[].message. A toast
            // cannot show it, so keep the list and open it in a dialog instead.
            setRecalculateFailures({
              period: result.period || filters.period,
              summary: message,
              records: result.failed_records || [],
            });
            setShowRecalcFailures(true);
            toast.warning(message, { autoClose: 8000 });
          }
          else if (result?.records_queued > 0) toast.info(message);
          else toast.success(message);
          reload(1);
        }
        else if (status === 'failed') { stopPolling(); setRecalculateJob(null); toast.error(`❌ ${error || 'Failed'}`); }
        else { setRecalculateJob((prev) => prev ? { ...prev, ...response } : null); }
      } catch (err) {
        // 404 = job not yet tracked (still queued) — keep polling silently
        const message = typeof err === 'string' ? err : err?.message;
        if (message?.includes('not found') || message?.includes('expired')) return;
        stopPolling(); setRecalculateJob(null); toast.error(`❌ ${message || 'Status check failed'}`);
      }
    }, 2500);
  }, [dispatch, stopPolling, reload, filters.period]);

  const handleRecalculatePeriod = useCallback(() => {
    confirmAlert({
      title: 'Recalculate Period',
      message: `Recalculate weights for <strong>${filters.period || 'all unreleased periods'}</strong>? Runs in background.`,
      closeOnEscape: true, closeOnClickOutside: false,
      buttons: [
        { label: 'Recalculate', variant: 'primary', onClick: async () => {
          try {
            const response = await dispatch(recalculateCsdPeriod({ period: filters.period || null })).unwrap();
            setRecalculateJob({ ...response, taskId: response.task_id });
            toast.info(response.already_running ? 'Recalculation is already queued.' : 'Recalculation queued.');
            startPolling(response.task_id);
          } catch (e) { toast.error(e?.message || '❌ Failed to start'); }
        }},
        { label: 'Cancel', onClick: () => {} },
      ],
    });
  }, [dispatch, filters.period, startPolling]);

  const handleDownload = useCallback(() => {
    dispatch(exportCsdForecasts({ ...buildFetchParams(), view_mode: viewMode }));
  }, [dispatch, buildFetchParams, viewMode]);

  // ── Status badge cell renderer ───────────────────────────────────────────────
  // Status is the forecast's own (Draft / Released / Backlog); deleting rows does
  // not change it. A forecast with rows on both sides says so — and links to the
  // other tab with the same search — instead of looking like two unrelated jobs.
  const StatusCell = useCallback((p) => {
    if (p.data?._is_group || p.data?._is_footer) return null;
    const chip = (cls, text) => <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold ${cls}`}>{text}</span>;
    const v = p.data?.status || (p.data?.released_to_planning_at ? 'released' : 'draft');
    let status;
    if (isDeletedView)        status = chip('tw-bg-red-50 tw-text-red-700', 'Deleted');
    else if (v === 'released') status = chip('tw-bg-emerald-100 tw-text-emerald-700', 'Released');
    else if (v === 'backlog')  status = chip('tw-bg-amber-100 tw-text-amber-700', 'Backlog');
    else                       status = chip('tw-bg-gray-100 tw-text-gray-500', 'Draft');

    const counts = p.data?.row_counts || {};
    const other = isDeletedView
      ? { n: counts.kept, text: 'still active', tab: '', hint: 'Other months of this project are still in the list — open them in All.' }
      : { n: counts.deleted, text: 'deleted', tab: 'deleted', hint: 'Some months of this project were deleted — open them in the Deleted tab.' };
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-1">
        {status}
        {!isDeletedView && p.data?.release_listed_at && (
          <span title="In the Release list — goes out with the next Release to Planning"
            className="tw-inline-flex tw-items-center tw-gap-0.5 tw-px-1.5 tw-rounded tw-bg-blue-50 tw-text-blue-700 tw-text-[11px] tw-font-semibold">
            <FaClipboardList size={9} aria-hidden="true" /> In list
          </span>
        )}
        {other.n > 0 && (
          <button type="button" onClick={() => setQuickFilter(other.tab)} title={other.hint}
            className="tw-px-1.5 tw-py-0 tw-rounded tw-border tw-border-solid tw-border-slate-300 tw-bg-white tw-text-[11px] tw-text-slate-600 hover:tw-bg-slate-50 tw-cursor-pointer">
            +{other.n} {other.text}
          </button>
        )}
      </span>
    );
  }, [isDeletedView]);

  // ── Project-view column defs ─────────────────────────────────────────────────
  // ── Unified column defs (building hidden in project mode) ───────────────────
  const columnDefs = useMemo(() => [
    // ── No. (sequence) + checkbox selection ─────────────────────────────────
    {
      // Checkbox + a 1–3 digit sequence only. suppressAutoSize: onFirstDataRendered
      // calls autoSizeAllColumns(), which otherwise widens this column far past 64px.
      headerName: 'No.', field: '_seq', width: 64, minWidth: 56, maxWidth: 72,
      pinned: 'left', sortable: false, resizable: false, suppressAutoSize: true, suppressSizeToFit: true,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      showDisabledCheckboxes: false,
      cellStyle: { textAlign: 'center', color: '#6B7280', fontSize: '11px', fontWeight: 500, paddingLeft: 4 },
      valueGetter: (p) => {
        if (p.data?._is_group)  return '';
        if (p.data?._is_footer) return '∑';
        return p.data?._seq ?? '';
      },
    },
    {
      headerName: 'Job information',
      children: [
        {
          headerName: 'Job no.', width: 130, pinned: 'left', sortable: false,
          valueGetter: (p) => {
            if (p.data?._is_footer) return '';
            if (p.data?._is_group)  return p.data.trans_date_group;
            return p.data?.project?.j_number || p.data?.project?.q_number || '';
          },
          cellRenderer: (p) => {
            if (p.data?._is_footer) return (
              <span className="tw-font-bold tw-text-amber-800 tw-text-xs tw-flex tw-items-center tw-gap-1">
                📌 Grand Total
                <span className="tw-text-gray-500 tw-font-normal">
                  ({p.data._project_count} project{p.data._project_count !== 1 ? 's' : ''})
                </span>
              </span>
            );
            if (p.data?._is_group) return (
              <span className="tw-flex tw-items-center tw-gap-2 tw-font-bold tw-text-primary-800 tw-text-sm">
                📅 {p.data.trans_date_group}
                <span className="tw-text-xs tw-font-normal tw-text-primary-500 tw-bg-primary-100 tw-px-1.5 tw-rounded-xl">
                  {p.data._project_count} project{p.data._project_count !== 1 ? 's' : ''}
                </span>
              </span>
            );
            return <Link to={`/crm/projects/${p.data?.project?.id}`} target="_blank" className="tw-text-blue-600 tw-font-mono tw-text-xs tw-no-underline hover:tw-underline">{p.value || '—'}</Link>;
          },
        },
        { headerName: 'Project name', flex: 1, minWidth: 160, valueGetter: (p) => p.data?._is_group ? '' : (p.data?.project?.name || '') },
        {
          headerName: 'Building', field: 'building_name', width: 80,
          hide: viewMode === 'project',
          valueFormatter: (p) => (p.data?._is_group || p.data?._is_footer) ? '' : (p.value || ''),
        },
        {
          headerName: 'Phase',
          field: 'phase',
          width: 90,
          valueFormatter: (p) => (p.data?._is_group || p.data?._is_footer) ? '' : (p.value || '')
        },
        {
          headerName: 'Expected Date of Release for Fabrication', field: 'expected_date', width: 130,
          valueFormatter: (p) => (p.data?._is_group || p.data?._is_footer || !p.value) ? '' : formatDate(p.value),
        },
        {
          headerName: '% Chance of Release', field: 'percent_change', width: 90,
          cellStyle: (p) => (p.data?._is_group || p.data?._is_footer) ? {} : ({ textAlign: 'center', fontWeight: 600, color: p.value > 70 ? '#15803d' : p.value > 40 ? '#92400e' : p.value != null ? '#991b1b' : '#94a3b8' }),
          valueFormatter: (p) => (p.data?._is_group || p.data?._is_footer || p.value == null) ? '' : `${p.value}%`,
        },
      ],
    },
    {
      headerName: 'Expected weight in Metric Tonnes', groupId: 'weight',
      headerClass: 'tw-bg-amber-300',
      children: [
        { headerName: 'B/ups', field: 'built_up_weight',    width: 125, minWidth: 110, ...WEIGHT_COLUMN, valueFormatter: (p) => fmt(p.value), cellStyle: (p) => p.data?._is_footer ? { fontWeight: 'bold', color: '#92400e' } : {} },
        { headerName: 'C/F',   field: 'cold_formed_weight', width: 125, minWidth: 110, ...WEIGHT_COLUMN, valueFormatter: (p) => fmt(p.value), cellStyle: (p) => p.data?._is_footer ? { fontWeight: 'bold', color: '#92400e' } : {} },
        { headerName: 'SH',    field: 'sheeting_weight',    width: 125, minWidth: 110, ...WEIGHT_COLUMN, valueFormatter: (p) => fmt(p.value), cellStyle: (p) => p.data?._is_footer ? { fontWeight: 'bold', color: '#92400e' } : {} },
      ],
    },
    { headerName: 'Status', width: 210, sortable: false, cellRenderer: StatusCell },
    {
      headerName: isDeletedView ? 'Deleted' : 'Released',
      children: [
        { headerName: 'Date', field: isDeletedView ? 'deleted_at' : 'released_at', width: 120, valueFormatter: (p) => (p.data?._is_group || p.data?._is_footer || !p.value) ? '' : formatDate(p.value) },
        { headerName: 'By',   field: isDeletedView ? 'deleted_by' : 'released_by', width: 95,  valueFormatter: (p) => (p.data?._is_group || p.data?._is_footer) ? '' : (p.value || '') },
      ],
    },
    {
      headerName: 'Sales',
      children: [
        { headerName: 'Incharge', field: 'sales_incharge',   width: 105, valueFormatter: (p) => (p.data?._is_group || p.data?._is_footer) ? '' : (p.value || '') },
        { headerName: 'Unit',     field: 'sales_unit_name',  width: 90,  valueFormatter: (p) => (p.data?._is_group || p.data?._is_footer) ? '' : (p.value || '') },
        { headerName: 'Region',   field: 'sales_unit_region', width: 100, valueFormatter: (p) => (p.data?._is_group || p.data?._is_footer) ? '' : (p.value || '') },
      ],
    },
    { headerName: 'Phase status', field: 'project_status_name', width: 130, valueFormatter: (p) => (p.data?._is_group || p.data?._is_footer) ? '' : (p.value || '') },
    { headerName: 'Remark', field: 'remark', width: 160, cellRenderer: (p) => (p.data?._is_group || p.data?._is_footer) ? null : <span className="tw-text-red-500 tw-text-xs">{p.value || ''}</span> },
    {
      headerName: 'Actions', width: 185, sortable: false, pinned: 'right',
      cellRenderer: (p) => {
        if (p.data?._is_group || p.data?._is_footer) return null;
        if (isDeletedView) return <DeletedActionCell data={p.data} onRestore={(d) => handleRestore([d])} onPurge={(d) => handlePurge([d])} />;
        return <ActionCell data={p.data} onEdit={handleBuildingEdit} onDelete={handleBuildingDelete} onRecalculate={handleBuildingRecalc} onBacklog={handleBacklogNow} />;
      },
    },
  ], [viewMode, isDeletedView, StatusCell, handleBuildingEdit, handleBuildingDelete, handleBuildingRecalc, handleBacklogNow, handleRestore, handlePurge]);

  const defaultColDef = useMemo(() => ({
    resizable: true, sortable: false, suppressMovable: false,
    wrapHeaderText: false, autoHeaderHeight: true,
  }), []);

  const getRowStyle = useCallback((p) => {
    if (p.data?._is_group)  return { background: '#d6e4f0', fontWeight: 'bold', color: '#1f3864', borderBottom: '1px solid #94a3b8' };
    if (p.data?._is_footer) return { background: '#FFF2CC', fontWeight: 'bold', borderTop: '2px solid #d97706' };
    if (p.data?.deleted_at)                 return { background: '#f8fafc', color: '#64748b' };
    if (p.data?.status === 'backlog')       return { background: '#fffbeb' };
    if (p.data?.released_to_planning_at || p.data?.released_at) return { background: '#f0fdf4' };
    return null;
  }, []);

  // ── Summary stats ─────────────────────────────────────────────────────────────
  const summary = csdState.allData?.summary;

  useEffect(() => { setPageReady(true); }, []);

  if (!pageReady) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-bg-gray-50 tw-gap-4">
        <LoadingSpinner size="lg" />
        <p className="tw-text-sm tw-text-gray-500 tw-animate-pulse">Loading Release Forecast CSD…</p>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4 tw-bg-gray-50 tw-min-h-screen">

      {/* Header — title left, the one primary action right. Keeping Release here
          rather than in the toolbar gives it a fixed position, so it never shifts
          when contextual bulk actions appear. */}
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-flex-wrap">
        <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
          <div className="tw-w-1 tw-h-9 tw-rounded-sm tw-bg-primary-600 tw-shrink-0" />
          <div className="tw-min-w-0">
            <h1 className="tw-mb-0 tw-font-bold tw-text-xl tw-text-slate-900 tw-truncate">
              3-Month Release Forecast — CSD
            </h1>
            <p className="tw-mb-0 tw-text-slate-500 tw-text-xs">
              CSD received forecasts based on Sales — 3-month building release plan
            </p>
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-gap-2">
          <Button onClick={() => setShowImport(true)} size="sm" variant="secondary" leftIcon={FaPlus}
                  title="Import data from Sales, Buildings, or Previous Month">
            New
          </Button>
          <Button onClick={handlePlanningReleaseClick}
                  disabled={loading || !filters.period || releaseCount === 0}
                  size="sm" leftIcon={FaRocket}
                  title={releaseListCount > 0
                    ? 'Release the projects of the Release list to Planning'
                    : 'The Release list is empty: this releases every unreleased project of the period'}>
            {releaseListCount > 0 ? `Release list to Planning (${releaseListCount})` : `Release all to Planning (${releaseCount})`}
          </Button>
        </div>
      </div>

      {/* Filter + Action bar */}
      <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-gray-200 tw-overflow-hidden">

        {/* ── Row 1: Filters ───────────────────────────────────────────────────
            One row, no field labels: each control states its own purpose through
            its placeholder, which removes a whole text row of height from a screen
            whose value is the grid below. */}
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
          <MonthYearPicker value={filters.period} onChange={(v) => setFilter('period', v)} clearable minWidth={140} />

          <div className="tw-relative tw-flex-1 tw-min-w-[220px]">
            <FaSearch size={10} className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400" />
            <input type="text" placeholder="Search project or job number…" value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="tw-h-9 tw-w-full tw-border tw-border-slate-300 tw-rounded tw-pl-7 tw-pr-3 tw-text-xs tw-outline-none focus:tw-border-primary-500 focus:tw-ring-1 focus:tw-ring-primary-200" />
          </div>

          <div className="tw-w-[220px]">
            <SalesUnitSelect
              value={filters.sales_unit_id}
              onChange={(v) => setFilter('sales_unit_id', v)}
              width="100%"
            />
          </div>

          {hasFilters && (
            <Button variant="ghost" size="sm" leftIcon={FaTimes} onClick={clearFilters}
                    className="tw-h-9 tw-text-slate-500">
              Clear
            </Button>
          )}
        </div>

        {/* ── Row 2: Scope + utilities ─────────────────────────────────────────
            Release Status and View Mode are both "pick exactly one", so both use
            the same segmented control. The old View Mode switch implied on/off and
            left it ambiguous which side was active. Utilities sit right, ordered
            least to most consequential. */}
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border-t tw-border-slate-100 tw-bg-slate-50/60 tw-px-3 tw-py-2">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <div className="tw-inline-flex tw-max-w-full tw-rounded tw-border tw-border-slate-300 tw-bg-white tw-p-0.5 tw-text-xs tw-font-medium">
            {TABS.map(({ key, label, Icon }) => (
              <button key={key} type="button" onClick={() => setQuickFilter(key)}
                aria-pressed={quickFilter === key}
                className={`tw-flex tw-h-7 tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-sm tw-border-0 tw-px-2.5 tw-cursor-pointer tw-transition-colors ${quickFilter === key ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-transparent tw-text-slate-600 hover:tw-bg-primary-50 hover:tw-text-primary-700'}`}>
                <Icon size={11} aria-hidden="true" />
                <span>{label}</span>
                {tabCounts && (
                  <span className={`tw-min-w-[18px] tw-rounded-full tw-px-1.5 tw-text-[10px] tw-font-semibold tw-leading-4 tw-tabular-nums ${quickFilter === key ? 'tw-bg-white/25 tw-text-white' : 'tw-bg-slate-100 tw-text-slate-600'}`}>
                    {tabCounts[key || 'all'] ?? 0}
                  </span>
                )}
              </button>
            ))}
          </div>
          {quickFilter === 'release_list' && releaseListCount > 0 && filters.period && (
            <Button variant="ghost" size="sm" onClick={handleClearReleaseList} className="tw-text-slate-600" title="Take every project of this period off the Release list">
              Clear list
            </Button>
          )}
          </div>

          <div className="tw-flex tw-items-center tw-gap-1">
            <div className="tw-inline-flex tw-rounded tw-border tw-border-slate-300 tw-bg-white tw-p-0.5 tw-text-xs tw-font-medium">
              {[
                { key: 'project',  label: 'Project',  Icon: MdAssignment },
                { key: 'building', label: 'Building', Icon: MdApartment },
              ].map(({ key, label, Icon }) => (
                <button key={key} type="button"
                  onClick={() => setViewMode(key)}
                  aria-pressed={viewMode === key}
                  className={`tw-flex tw-h-7 tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-sm tw-border-0 tw-px-2.5 tw-cursor-pointer tw-transition-colors ${viewMode === key ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-transparent tw-text-slate-600 hover:tw-bg-primary-50 hover:tw-text-primary-700'}`}>
                  <Icon size={12} aria-hidden="true" />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            <div className="tw-w-px tw-h-5 tw-bg-slate-200" />

            {recalculateFailures?.records?.length > 0 && (
              <Button variant="secondary" size="sm" leftIcon={FaExclamationCircle}
                onClick={() => setShowRecalcFailures(true)}
                className="tw-text-amber-700 tw-border-amber-300 hover:tw-bg-amber-50"
                title="Why the last recalculation skipped these records">
                Failures ({recalculateFailures.records.length})
              </Button>
            )}
            <Button onClick={() => reload()} disabled={loading} size="sm" variant="secondary" title="Refresh">
              <FaSync size={11} className={loading ? 'tw-animate-spin' : ''} />
            </Button>
            <Button onClick={handleDownload} disabled={loading || exportLoading} size="sm" variant="secondary"
                    leftIcon={MdDownload} title="Export Excel">
              Export
            </Button>
            {/* Label no longer carries the period — it is already set in the filter
                above, and the dynamic text used to clip to "Recalc…". */}
            <Button onClick={handleRecalculatePeriod} disabled={loading || !!recalculateJob}
                    size="sm" variant="secondary"
                    title={`Recalculate weights for ${filters.period || 'all periods'}`}>
              <FaCalculator size={11} className={recalculateJob ? 'tw-animate-pulse' : ''} />
              <span className="tw-ml-1.5">{recalculateJob ? 'Recalculating' : 'Recalculate'}</span>
            </Button>
          </div>
        </div>

        {/* ── Contextual selection bar ─────────────────────────────────────────
            Bulk actions live here instead of the toolbar so selecting rows never
            reflows the toolbar and moves the other buttons under the cursor. */}
        {selectedRows.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-t tw-border-primary-100 tw-bg-primary-50 tw-px-3 tw-py-2">
            <span className="tw-text-xs tw-font-semibold tw-text-primary-800">
              {selectedRows.length} row{selectedRows.length !== 1 ? 's' : ''} selected
            </span>
            {hiddenSelectedCount > 0 && (
              <span className="tw-text-xs tw-text-primary-600" title="Kept while you search or filter — cleared after each action">
                ({hiddenSelectedCount} not shown by current filter)
              </span>
            )}
            <div className="tw-w-px tw-h-4 tw-bg-primary-200" />
            {isDeletedView && (
              <>
                <Button variant="secondary" onClick={() => handleRestore(selectedRows)} size="sm" leftIcon={FaUndoAlt}
                        className="tw-text-emerald-700 tw-border-emerald-300 hover:tw-bg-emerald-50">
                  Restore ({selectedRows.length})
                </Button>
                <Button variant="danger" onClick={() => handlePurge(selectedRows)} size="sm" leftIcon={FaTrash}>
                  Delete permanently ({selectedRows.length})
                </Button>
              </>
            )}
            {!isDeletedView && listableIds.length > 0 && (
              <Button variant="secondary" onClick={handleAddToReleaseList} size="sm" leftIcon={FaClipboardList}
                      className="tw-text-blue-700 tw-border-blue-300 hover:tw-bg-blue-50">
                Add to release list ({listableIds.length})
              </Button>
            )}
            {!isDeletedView && listedIds.length > 0 && (
              <Button variant="secondary" onClick={() => handleRemoveFromReleaseList(listedIds)} size="sm" leftIcon={FaMinusCircle}>
                Remove from list ({listedIds.length})
              </Button>
            )}
            {!isDeletedView && backlogableCount > 0 && (
              <Button variant="secondary" onClick={handleBulkBacklog} size="sm" leftIcon={FaBoxOpen}
                      className="tw-text-amber-700 tw-border-amber-300 hover:tw-bg-amber-50">
                Move to backlog ({backlogableCount})
              </Button>
            )}
            {!isDeletedView && deletableCount > 0 && (
              <Button variant="danger" onClick={handleBulkDelete} size="sm" leftIcon={FaTrash}>
                Delete ({deletableCount})
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={clearSelection}
                    className="tw-ml-auto tw-text-slate-600">
              Clear selection
            </Button>
          </div>
        )}

        {recalculateJob && (
          <div className="tw-mx-3 tw-mb-3 tw-flex tw-items-center tw-gap-3 tw-rounded tw-border tw-border-blue-200 tw-bg-blue-50 tw-px-3 tw-py-2" role="status" aria-live="polite">
            <span className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded tw-bg-blue-100 tw-text-blue-600">
              <FaSpinner size={14} className="tw-animate-spin" />
            </span>
            <div className="tw-min-w-0 tw-flex-1">
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <span className="tw-text-xs tw-font-semibold tw-text-slate-800">
                  {recalculateJob.status === 'running' ? 'Recalculating released estimate data' : 'Waiting for Sidekiq worker'}
                </span>
                {recalculateJob.progress?.total > 0 && (
                  <span className="tw-text-[11px] tw-font-medium tw-text-blue-700">
                    {recalculateJob.progress.processed}/{recalculateJob.progress.total}
                  </span>
                )}
              </div>
              <div className="tw-mt-1 tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-blue-100">
                <div
                  className={`tw-h-full tw-rounded-full tw-bg-blue-500 tw-transition-all ${recalculateJob.progress?.total ? '' : 'tw-w-1/3 tw-animate-pulse'}`}
                  style={recalculateJob.progress?.total ? { width: `${Math.min(100, (recalculateJob.progress.processed / recalculateJob.progress.total) * 100)}%` } : undefined}
                />
              </div>
              <p className="tw-mb-0 tw-mt-1 tw-text-[10px] tw-text-slate-500">
                Period {recalculateJob.period || filters.period}. You can keep working while this runs in the background.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-bg-white tw-overflow-hidden">
          <div className="tw-px-5 tw-py-4 tw-bg-gradient-to-r tw-from-primary-50 tw-to-blue-50 tw-border-b tw-border-gray-100">
            <h3 className="tw-text-xs tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wide tw-flex tw-items-center tw-gap-2 tw-mb-3">
              <span className="tw-w-1 tw-h-4 tw-bg-primary-500 tw-rounded-xl" /> Summary
            </h3>
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-3">
              {[
                { label: 'Total Projects', value: summary.project_count || 0, icon: <MdNumbers />, color: 'tw-text-primary-600', unit: 'projects' },
                { label: 'Grand Total',    value: formatNumber((summary.month1_total || 0) + (summary.month2_total || 0) + (summary.month3_total || 0)), icon: <MdScale />, color: 'tw-text-green-700', unit: 'MT' },
                { label: monthLabels[0] || 'M+1', value: formatNumber(summary.month1_total || 0), icon: <MdScale />, color: 'tw-text-amber-700', unit: 'MT' },
                { label: monthLabels[1] || 'M+2', value: formatNumber(summary.month2_total || 0), icon: <MdScale />, color: 'tw-text-blue-700',  unit: 'MT' },
                { label: monthLabels[2] || 'M+3', value: formatNumber(summary.month3_total || 0), icon: <MdScale />, color: 'tw-text-purple-700', unit: 'MT' },
              ].map(({ label, value, icon, color, unit }) => (
                <div key={label} className="tw-rounded tw-bg-white tw-border tw-border-gray-100 tw-p-3 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <div>
                      <div className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1">{label}</div>
                      <div className={`tw-font-bold tw-text-xl ${color}`}>{value}</div>
                      <div className="tw-text-xs tw-text-gray-400 tw-mt-0.5">{unit}</div>
                    </div>
                    <div className="tw-text-3xl tw-text-gray-200">{icon}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="tw-relative" style={{ width: '100%', height: 'max(320px, calc(100dvh - 380px))', minHeight: 400, '--ag-font-size': '12px' }}>
        {loading && <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/60 tw-z-10"><LoadingSpinner /></div>}
        {exportLoading && <LoadingSpinner fullScreen message="Exporting Excel…" />}
        <AgGridReact
          key={`${viewMode}`}
          ref={gridRef}
          rowData={gridRows}
          // "No Rows To Show" under the loading spinner read as an empty period.
          suppressNoRowsOverlay={loading}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowStyle={getRowStyle}
          isRowSelectable={(p) => !p.data?._is_group && !p.data?._is_footer}
          rowSelection="multiple"
          suppressRowClickSelection
          suppressPaginationPanel
          rowHeight={26}
          getRowHeight={(p) => (p.data?._is_group || p.data?._is_footer) ? 36 : 26}
          headerHeight={36}
          groupHeaderHeight={28}
          columnGroupHeaderHeight={32}
          onSelectionChanged={onSelectionChanged}
          onRowDataUpdated={restoreSelection}
          getRowId={(p) => p.data?.rowId ? String(p.data.rowId) : String(p.data.id)}
          onFirstDataRendered={(e) => { e.api.autoSizeAllColumns(); restoreSelection(e); }}
          pinnedBottomRowData={pinnedBottomRow}
        />
      </div>

      {/* Pagination */}
      <div className="tw-flex tw-items-center tw-justify-between tw-bg-white tw-rounded tw-px-4 tw-py-2.5 tw-shadow-sm tw-border tw-border-gray-200">
        <span className="tw-text-xs tw-text-gray-500">
          Records: <span className="tw-font-semibold tw-text-gray-800">{totalRows.toLocaleString()}</span>
        </span>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalRows}
          perPage={perPage}
          onPageChange={(pg) => dispatch(setCsdPage({ key: 'allData', page: pg }))}
          onPageSizeChange={(sz) => { dispatch(setCsdPageSize({ key: 'allData', pageSize: sz })); dispatch(setCsdPage({ key: 'allData', page: 1 })); }}
          disabled={loading}
          compact
          pageSizeOptions={[20, 50, 100, 200]}
        />
      </div>

      {/* Edit form */}
      {showEditForm && (
        <CsdForecastEditForm
          show={showEditForm}
          record={selectedRecord}
          onHide={() => { setShowEditForm(false); setSelectedRecord(null); }}
          onSaved={() => { setShowEditForm(false); setSelectedRecord(null); reload(1); }}
        />
      )}

      {/* Planning Release Dialog */}
      {planningReleaseTarget && (
        <PlanningReleaseConfirmDialog
          target={planningReleaseTarget}
          onConfirm={handlePlanningReleaseConfirm}
          onCancel={() => setPlanningReleaseTarget(null)}
        />
      )}

      {showRecalcFailures && recalculateFailures?.records?.length > 0 && (
        <RecalculateFailuresDialog
          period={recalculateFailures.period}
          summary={recalculateFailures.summary}
          failures={recalculateFailures.records}
          onHide={() => setShowRecalcFailures(false)}
        />
      )}

      {/* Import Modal */}
      <CsdImportModal
        show={showImport}
        onHide={() => setShowImport(false)}
        period={filters.period}
        onSuccess={() => reload(1)}
      />
    </div>
  );
};

export default ReleaseForecastCsdPage;
